import axios from 'axios';
import { auth } from '../config/firebase';
import { getIdToken } from 'firebase/auth';
import { generateCodeVerifier, generateCodeChallenge } from '../controllers/pkceUtils';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';

const API_BASE_URL = 'https://api.meethue.com/v2';

let accessToken = localStorage.getItem('hue_access_token');
let refreshToken = localStorage.getItem('hue_refresh_token');
let username = localStorage.getItem('hue_username');

let codeVerifier = localStorage.getItem('pkce_code_verifier');

const db = getFirestore();

const getCurrentUserToken = async () => {
  const user = auth.currentUser;
  if (user) {
    return await getIdToken(user);
  }
  throw new Error('Aucun utilisateur connecté');
};

export const getStoredToken = async () => {
  const tokenDoc = await getDoc(doc(db, 'tokens', 'hue'));
  if (tokenDoc.exists()) {
    const tokenData = tokenDoc.data();
    const expirationDate = tokenData.expirationDate.toDate();
    if (expirationDate > new Date()) {
      return tokenData;
    }
  }
  return null;
};

export const getAuthorizationCode = async () => {
  const clientId = 'oAjdGwYW4PspRUCM7z6O129w3abtxHER';
  const redirectUri = 'https://theapp-428906.web.app/hue';
  const state = 'YOUR_RANDOM_STATE'; // Utilisé pour protéger contre les attaques CSRF

  codeVerifier = generateCodeVerifier();
  localStorage.setItem('pkce_code_verifier', codeVerifier);
  console.info("codeVerifier", codeVerifier);

  const codeChallenge = await generateCodeChallenge(codeVerifier);

  const authorizationUrl = `${API_BASE_URL}/oauth2/authorize?client_id=${clientId}&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}&code_challenge=${codeChallenge}&code_challenge_method=S256`;

  // Redirige l'utilisateur vers l'URL d'autorisation
  window.location.href = authorizationUrl;
};

export const getAccessToken = async (authorizationCode) => {
  const clientId = 'oAjdGwYW4PspRUCM7z6O129w3abtxHER';
  const redirectUri = 'https://theapp-428906.web.app/hue';
  const codeVerifier = localStorage.getItem('pkce_code_verifier');
  const clientSecret = 'w1xLJhWJzrA02sve';
  console.info(codeVerifier, authorizationCode);
  try {
    const functions = getFunctions();
    const getAccessTokenCallable = httpsCallable(functions, 'getAccessToken');
    
    const result = await getAccessTokenCallable({
      authorizationCode,
      clientId,
      clientSecret,
      redirectUri,
      codeVerifier
    });
    
    console.info(result);
    const accessToken = result.data.access_token;
    const refreshToken = result.data.refresh_token;

    await linkApplication(accessToken);
  } catch (error) {
    console.error('Erreur lors de la récupération du jeton d\'accès:', error);
    throw error;
  }
};

export const refreshAccessToken = async () => {
  const clientId = 'oAjdGwYW4PspRUCM7z6O129w3abtxHER';
  const clientSecret = 'w1xLJhWJzrA02sve';
  const refreshToken = localStorage.getItem('hue_refresh_token');

  try {
    const functions = getFunctions();
    const refreshAccessTokenCallable = httpsCallable(functions, 'refreshAccessToken');
    
    const result = await refreshAccessTokenCallable({
      refreshToken,
      clientId,
      clientSecret
    });
    
    console.info(result);
    const accessToken = result.data.access_token;
    const newRefreshToken = result.data.refresh_token;
  } catch (error) {
    console.error('Erreur lors du rafraîchissement du jeton d\'accès:', error);
    throw error;
  }
};

const linkApplication = async (accessToken) => {
  const appName = 'theapp';
  try {
    const functions = getFunctions();

    // Step 1: Link button
    const linkButtonCallable = httpsCallable(functions, 'linkButton');
    await linkButtonCallable({ accessToken });

    // Step 2: Register application and get username
    const registerAppCallable = httpsCallable(functions, 'registerApp');
    const result = await registerAppCallable({ accessToken, appName });
    username = result.data.username;
    localStorage.setItem('hue_username', username);
    console.info("username", username);

  } catch (error) {
    console.error('Erreur lors de la liaison de l\'application:', error);
    throw error;
  }
};

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

// Function to refresh access token
axiosInstance.interceptors.request.use(async (config) => {
  if (!accessToken) {
    await refreshAccessToken();
  }
  config.headers.Authorization = `Bearer ${accessToken}`;
  config.headers['hue-application-key'] = username;
  return config;
});

export const fetchLights = async (accessToken, username) => {
  try {
    const functions = getFunctions();
    const fetchLightsCallable = httpsCallable(functions, 'fetchLights');
    const result = await fetchLightsCallable({ accessToken, username });
    console.info(result.data.data);
    return result.data.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des lumières:', error);
    throw error;
  }
};

// Function to fetch groups using callable function
export const fetchGroups = async () => {
  try {
    const functions = getFunctions();
    const fetchGroupsCallable = httpsCallable(functions, 'fetchGroups');
    const result = await fetchGroupsCallable({ accessToken, username });
    return result.data.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des pièces:', error);
    throw error;
  }
};

// Function to turn on a light using callable function
export const turnOnGroup = async (groupId, color) => {
  try {
    const functions = getFunctions();
    const turnOnGroupCallable = httpsCallable(functions, 'turnOnGroup');
    const result = await turnOnGroupCallable({ accessToken, username, groupId, color });
    console.info(result.data);
    return result.data;
  } catch (error) {
    console.error('Erreur lors de l\'allumage du groupe:', error);
    throw error;
  }
};

export const turnOffGroup = async (groupId) => {
  try {
    const functions = getFunctions();
    const turnOffGroupCallable = httpsCallable(functions, 'turnOffGroup');
    const result = await turnOffGroupCallable({ accessToken, username, groupId });
    return result.data;
  } catch (error) {
    console.error('Erreur lors de l\'extinction du groupe:', error);
    throw error;
  }
};

export const turnOnLight = async (lightId) => {
  try {
    const functions = getFunctions();
    const turnOnLightCallable = httpsCallable(functions, 'turnOnLight');
    const result = await turnOnLightCallable({ accessToken, username, lightId });
    return result.data;
  } catch (error) {
    console.error('Erreur lors de l\'allumage de la lumière:', error);
    throw error;
  }
};

export const turnOffLight = async (lightId) => {
  try {
    const functions = getFunctions();
    const turnOffLightCallable = httpsCallable(functions, 'turnOffLight');
    const result = await turnOffLightCallable({ accessToken, username, lightId });
    return result.data;
  } catch (error) {
    console.error('Erreur lors de l\'extinction de la lumière:', error);
    throw error;
  }
};

export const fetchRooms = async () => {
  try {
    const functions = getFunctions();
    const fetchRoomsCallable = httpsCallable(functions, 'fetchRooms');
    const result = await fetchRoomsCallable({ accessToken, username });
    return result.data.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des pièces:', error);
    throw error;
  }
};